import React, { useEffect, useState, useContext } from "react";
import toast from "react-hot-toast";
import api from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function OwnerDashboardPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    pendingDeals: [],
    completedDeals: [],
    totalDeals: 0,
    completedCount: 0,
  });

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role !== "OWNER") {
      navigate("/");
      return;
    }

    async function load() {
      try {
        setLoading(true);
        const res = await api.get("/dashboard/owner");
        setData(res.data);
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || "Failed to load owner dashboard");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [user, navigate]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 py-10">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-semibold">Owner Dashboard</h1>
      <p className="text-gray-500">Manage your rooms and deal requests</p>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-6 mt-6">
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <div className="text-3xl font-bold">{data.totalDeals}</div>
          <div className="text-gray-500">Total Deals</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <div className="text-3xl font-bold">{data.completedCount}</div>
          <div className="text-gray-500">Completed Deals</div>
        </div>
      </div>

      {/* Pending / active deal requests */}
      <div className="mt-8 bg-white rounded-xl shadow p-6">
        <h2 className="font-semibold mb-4">Active Deal Requests</h2>

        {data.pendingDeals.length === 0 ? (
          <p className="text-sm text-gray-500">
            You have no active deal requests right now.
          </p>
        ) : (
          data.pendingDeals.map((d) => (
            <div
              key={d.id}
              className="border-b last:border-0 py-3 flex items-center justify-between"
            >
              <div>
                <div className="font-semibold">{d.listing.title}</div>
                <div className="text-gray-500 text-sm">
                  Rs. {d.rentAmount} • Status: {d.status}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Seeker: {d.seeker?.fullName || "N/A"} •{" "}
                  {d.seeker?.email || "No email"}
                </div>
                <div className="text-xs text-gray-400">
                  Seeker confirmed: {d.seekerConfirmed ? "Yes" : "No"} | Owner
                  confirmed: {d.ownerConfirmed ? "Yes" : "No"} | Paid:{" "}
                  {d.paid ? "Yes" : "No"}
                </div>
              </div>

              <button
                onClick={() => navigate(`/deal/${d.id}`)}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-500 text-white hover:bg-indigo-600"
              >
                View Details
              </button>
            </div>
          ))
        )}
      </div>

      {/* Completed deals */}
      <div className="mt-8 bg-white rounded-xl shadow p-6">
        <h2 className="font-semibold mb-4">Completed Deals</h2>

        {data.completedDeals.length === 0 ? (
          <p className="text-sm text-gray-500">
            No completed deals yet.
          </p>
        ) : (
          data.completedDeals.map((d) => (
            <div
              key={d.id}
              className="border-b last:border-0 py-3 flex items-center justify-between"
            >
              <div>
                <div className="font-semibold">{d.listing.title}</div>
                <div className="text-gray-500 text-sm">
                  Rs. {d.rentAmount} • Status: {d.status}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Seeker: {d.seeker?.fullName || "N/A"} •{" "}
                  {d.seeker?.email || "No email"}
                </div>
              </div>
              <div className="text-primary font-semibold">
                Rs. {d.serviceCharge} fee
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
